import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import {
  SIGNING_CASE_STATUS,
  SIGNING_PROVIDER,
} from "../../../constants/SigningProviderConstants";
import {
  QES_CURRENT_STAGE,
  getNPQESStepperValue,
  getJPQESStepperValue,
} from "../../../constants/userConstants";
import { formConstants } from "../../../helpers/vorgange";
import { manualRequestFormActions } from "../../../store/actions/vorgange/manualRequestFormActions";
import { signingProviderActions } from "../../../store/actions/vorgange/signingProviderActions";

//core
import MDCButton from "../../core/MDCButton";
import MDCRadioButton from "../../core/MDCRadioButton";
import SignerSelectionScreen from "./SignerSelectionScreen";
import QESIdentitySetupForm from "./QESIdentitySetupForm";
import QESStepperScreen from "./QESStepperScreen";
import { getSigningFlow, rememberSigningFlow } from "./signingFlowMemory";
import ErrorText from "../../shared/ErrorText";

const useStyles = makeStyles((theme) => ({
  container: { color: theme.palette.MDCColors.textColor },
  entryPanel: {
    width: "80%",
    maxWidth: "38rem",
    margin: "0 auto",
    padding: "1rem 1.25rem",
  },
  sectionTitle: {
    fontWeight: 700,
    marginBottom: ".75rem",
    textAlign: "left",
    font: "normal normal bold 13px/15px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
    opacity: 1,
  },
  companyGrid: {
    display: "grid",
    gap: ".75rem",
    minWidth: 0,
    "& .MuiFormControlLabel-root": {
      alignItems: "flex-start",
      display: "flex",
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      margin: 0,
      padding: ".875rem",
      border: `1px solid ${theme.palette.MDCColors.color21}`,
      borderRadius: "3px",
    },
    "& .MuiFormControlLabel-root .MuiFormControlLabel-label": {
      flex: "1 1 0%",
      minWidth: 0,
      width: 0,
      maxWidth: "100%",
      overflow: "hidden",
    },
  },
  companyOptionLabel: {
    display: "block",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  companyName: {
    fontWeight: 700,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
    display: "block",
    textAlign: "left",
    font: "normal normal bold 14px/17px Fira Sans",
    letterSpacing: "0px",
    color: theme.palette.MDCColors.textColor,
    opacity: 1,
  },
  companyAddress: {
    display: "block",
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  hraHrb: {
    display: "block",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
  },

  hraHrbValue: {
    display: "inline-block",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    verticalAlign: "bottom",
  },
  signerList: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "0 .75rem",
    minWidth: 0,
    padding: ".75rem",
    border: `1px solid ${theme.palette.MDCColors.color21}`,
  },
  navigationActions: {
    display: "flex",
    justifyContent: "space-between",
    gap: ".5rem",
    marginTop: "1.25rem",
  },
}));

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SigningStartScreen = ({
  onDownloadDocument,
  provider,
  requestId,
  personenform,
  isDetailDialog,
  halterFormValues,
  firstName,
  lastName,
  companyName,
  holderType,
  companyAddress,
  documentsAvailable,
  addressDocumentAvailable,
  onBackToProviders,
  onQESStartAttempt = null,
  onCancelSigning,
  onDownloadPrefilledDocuments,
  flowScope = "manual",
  isExceptionalJP = false,
  isPPOA = false,
  ppoaSigningStatus = null,
  onGetPPOASigningStatus = null,
  documents = [], // Documents from parent (API response)
  onSigningComplete = null, // Callback when signing completes
  disabled = false, // Disable entire signing flow
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { signingCase, loading, validationMessages } = useSelector(
    (state) => state.signingProviderReducer
  );
  const reduxVollmachtSigningStatus = useSelector(
    (state) => state.manualRequestFormReducer?.vollmachtSigningStatus
  );
  const vollmachtSigningStatus = isPPOA
    ? ppoaSigningStatus
    : reduxVollmachtSigningStatus;

  const currentStage = vollmachtSigningStatus?.currentStage;
  const stageStatus = vollmachtSigningStatus?.stageStatus;

  const currentSigningCase =
    signingCase?.requestId === requestId ? signingCase : null;
  const providerKey = provider?.id || provider?.key || provider?.providerId;
  const savedScreen = getSigningFlow(requestId).screen;
  const savedState =
    savedScreen?.providerKey === providerKey &&
    savedScreen?.flowScope === flowScope
      ? savedScreen
      : {};
  const isLegalPerson = personenform === formConstants.companyPerson;
  const companyOption = {
    id: "holder-company",
    name: companyName || holderType,
    address: [
      [companyAddress?.street, companyAddress?.houseNumber]
        .filter(Boolean)
        .join(" "),
      [companyAddress?.postalCode, companyAddress?.city]
        .filter(Boolean)
        .join(" "),
    ]
      .filter(Boolean)
      .join(", "),
  };
  const [email, setEmail] = useState(savedState.email || "");
  const [emailError, setEmailError] = useState("");
  const [hrab, setHrab] = useState(savedState.hrab ?? "");
  const [hrabError, setHrabError] = useState("");
  const [companyStep, setCompanyStep] = useState(
    savedState.companyStep || "entry"
  );
  const [selectedCompany, setSelectedCompany] = useState(
    savedState.selectedCompany ?? companyOption
  );
  const [flowError, setFlowError] = useState("");
  const [verifyingCompany, setVerifyingCompany] = useState(false);
  const [showProgressScreen, setShowProgressScreen] = useState(false);
  const [isSubmittingNP, setIsSubmittingNP] = useState(false);

  // Use documents from props (passed from parent), or fallback to API response
  // For JP: API returns documents at providerDetails.signedDocuments
  const docsToDisplay =
    documents && documents.length > 0
      ? documents
      : vollmachtSigningStatus?.providerDetails?.signedDocuments || [];

  const hasSignatories =
    vollmachtSigningStatus?.providerDetails?.signatories?.length > 0;

  const isCompletedScreen =
    currentStage === "sign" && stageStatus?.toLowerCase() === "completed";

  useEffect(() => {
    if (!requestId) return; // Guard against undefined requestId
    rememberSigningFlow(requestId, {
      screen: {
        flowScope,
        providerKey,
        email,
        hrab,
        companyStep,
        selectedCompany,
      },
    });
  }, [
    requestId,
    flowScope,
    providerKey,
    email,
    hrab,
    companyStep,
    selectedCompany,
  ]);

  useEffect(() => {
    if (
      ![
        SIGNING_CASE_STATUS.CREATED,
        SIGNING_CASE_STATUS.PENDING_VERIFICATION,
        SIGNING_CASE_STATUS.AWAITING_SIGNER_SELECTION,
        SIGNING_CASE_STATUS.PROCESSING,
      ].includes(currentSigningCase?.status)
    )
      return undefined;
    if (!requestId) return undefined; // Guard against undefined requestId
    const timer = setInterval(
      () => dispatch(signingProviderActions.getSigningCase(requestId)),
      1500
    );
    return () => clearInterval(timer);
  }, [requestId, currentSigningCase?.status]);

  // Call onSigningComplete callback when signing completes
  useEffect(() => {
    if (isCompletedScreen && onSigningComplete) {
      onSigningComplete({
        requestId,
        documents: docsToDisplay,
        stageStatus,
      });
    }
  }, [
    isCompletedScreen,
    onSigningComplete,
    requestId,
    stageStatus,
    docsToDisplay,
  ]);

  // For JP case: handle screen transitions based on currentStage from API response
  useEffect(() => {
    if (!isLegalPerson || !currentStage) return; // Only for standard JP case

    if (currentStage === "verify" && !isExceptionalJP) {
      if (stageStatus?.toLowerCase() === "completed") {
        // Verification completed: move to signer selection
        setCompanyStep("signer-selection");
      } else {
        // Verification in progress: show company selection (will be disabled via UI)
        setCompanyStep("company-selection");
      }
    } else if (
      currentStage === "notification" ||
      currentStage === "ident" ||
      currentStage === "sign"
    ) {
      // During signing: show progress screen with stepper
      setShowProgressScreen(true);
    }
  }, [currentStage, stageStatus, isLegalPerson, isExceptionalJP]);

  const startCompanyVerification = async () => {
    onQESStartAttempt?.();

    // For exceptional JP case (individual address + self-employed), validate email
    if (isExceptionalJP) {
      if (isSubmittingNP) {
        return; // Prevent multiple submissions
      }

      if (!emailPattern.test(email)) {
        setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
        return;
      }

      setIsSubmittingNP(true);
      setEmailError("");
      setFlowError("");

      try {
        // Wait for verification response before proceeding
        const resolvedProviderId =
          provider?.id ||
          provider?.key ||
          provider?.providerId ||
          SIGNING_PROVIDER.YOUTRUST;

        await dispatch(
          signingProviderActions.startVerification(
            requestId,
            resolvedProviderId,
            {
              halterType: "juristische",
              emailId: email,
            },
            isPPOA
          )
        );

        // Fetch status after verification for immediate UI update
        if (isPPOA && onGetPPOASigningStatus) {
          onGetPPOASigningStatus();
        } else {
          dispatch(
            manualRequestFormActions.getVollmachtSigningStatus(requestId)
          );
        }

        // Only proceed to progress screen after successful response
        setIsSubmittingNP(false);
        setShowProgressScreen(true);
      } catch (error) {
        const errorFieldMap =
          error?.response?.data?.errorFieldMap || error?.data?.errorFieldMap;
        const emailValidationError = Object.entries(errorFieldMap || {}).find(
          ([field]) =>
            field === "additionalFields.emailId" ||
            field === "emailId" ||
            field === "email"
        )?.[1];
        if (emailValidationError) {
          setEmailError(emailValidationError);
        }
        setIsSubmittingNP(false);
        setShowProgressScreen(false);
        setFlowError(
          emailValidationError
            ? ""
            : error?.response?.data?.message ||
                error?.data?.message ||
                error?.message ||
                "Die Signaturverifizierung konnte nicht gestartet werden."
        );
      }
      return;
    }

    // Standard JP flow - company verification via HRA/HRB
    if (!hrab.trim()) {
      setHrabError("Bitte geben Sie eine gültige HRA/HRB-Nummer ein.");
      return;
    }
    setHrabError("");
    setFlowError("");
    setVerifyingCompany(false);
    setCompanyStep("company-selection");

    const resolvedProviderId =
      provider?.id ||
      provider?.key ||
      provider?.providerId ||
      SIGNING_PROVIDER.YOUTRUST;

    dispatch(
      signingProviderActions.startVerification(
        requestId,
        resolvedProviderId,
        {
          halterType: "juristische",
          hraHrbNumber: hrab,
        },
        isPPOA
      )
    )
      .then(() => {
        // Fetch status after verification for immediate UI update
        if (isPPOA && onGetPPOASigningStatus) {
          onGetPPOASigningStatus();
        } else {
          dispatch(
            manualRequestFormActions.getVollmachtSigningStatus(requestId)
          );
        }
      })
      .catch(() => {
        setFlowError();
        // "Die Unternehmensverifizierung konnte nicht gestartet werden."
      });
  };
  const continueToSigners = () => {
    if (!selectedCompany) {
      setFlowError("Bitte wählen Sie ein Unternehmen aus.");
      return;
    }

    // Check if signatories are available from API
    const apiSignatories = vollmachtSigningStatus?.providerDetails?.signatories;
    if (!apiSignatories || apiSignatories.length === 0) {
      setFlowError(
        "Die vertretungsberechtigten Personen sind noch nicht verfügbar."
      );
      return;
    }

    setFlowError("");
    setCompanyStep("signer-selection");
  };
  const startNaturalPersonSigning = async () => {
    if (!requestId) {
      setFlowError("Anfrage-ID fehlt. Bitte laden Sie die Seite neu.");
      return;
    }

    if (isSubmittingNP) {
      return; // Prevent multiple submissions
    }

    if (!emailPattern.test(email)) {
      setEmailError("Bitte geben Sie eine gültige E-Mail-Adresse ein.");
      return;
    }

    setIsSubmittingNP(true);
    setEmailError("");
    setFlowError("");

    try {
      const resolvedProviderId =
        provider?.id ||
        provider?.key ||
        provider?.providerId ||
        SIGNING_PROVIDER.YOUTRUST;

      const signingCaseResponse = await dispatch(
        signingProviderActions.createSigningCase(requestId, {
          providerId: resolvedProviderId,
          providerName: provider?.name || provider?.displayName || "YouTrust",
          personenform,
          firstName,
          lastName,
          email,
          documentsAvailable,
          addressDocumentAvailable,
        })
      );

      // Verify signing case was created successfully
      if (!signingCaseResponse || signingCaseResponse?.status >= 400) {
        setFlowError("Der Signaturfall konnte nicht erstellt werden.");
        setIsSubmittingNP(false);
        return;
      }

      // Fetch status for immediate UI update
      if (isPPOA && onGetPPOASigningStatus) {
        onGetPPOASigningStatus();
      } else {
        dispatch(manualRequestFormActions.getVollmachtSigningStatus(requestId));
      }

      // Proceed to progress screen only after successful response
      setIsSubmittingNP(false);
      setShowProgressScreen(true);
    } catch (error) {
      console.error("Error in startNaturalPersonSigning:", error);
      setFlowError(
        error?.message ||
          "Ein Fehler ist beim Signaturprozess aufgetreten. Bitte versuchen Sie es erneut."
      );
      setIsSubmittingNP(false);
    }
  };
  const cancelSigning = () => {
    if (!requestId) {
      return Promise.reject("Anfrage-ID fehlt");
    }
    return (
      onCancelSigning
        ? onCancelSigning()
        : dispatch(manualRequestFormActions.deleteQESDetails(requestId))
    )
      .then(() => dispatch(signingProviderActions.cancelSigningCase(requestId)))
      .then(() => {
        onBackToProviders();
      });
  };

  const downloadSignedDocument = (docId, fileName) => {
    if (onDownloadDocument) {
      return onDownloadDocument(docId, fileName);
    }
  };
  const downloadPrefilledDocuments = () => {
    if (!requestId) {
      toast.error(
        "Anfrage-ID fehlt. Die Dokumente können nicht heruntergeladen werden."
      );
      return Promise.reject("requestId is undefined");
    }
    return dispatch(
      signingProviderActions.downloadPrefilledDocuments(requestId, isPPOA)
    ).then((response) => {
      if (response.status !== 200) return;
      const contentDisposition = response.headers?.["content-disposition"];
      const fileNameMatch = contentDisposition?.match(
        /filename\*?=(?:UTF-8'')?([^;]+)/i
      );
      const fileName = fileNameMatch
        ? decodeURIComponent(fileNameMatch[1].replace(/["']/g, "").trim())
        : `Signaturdokumente-${requestId}.zip`;
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    });
  };

  // Stepper logic: uses getJPQESStepperValue for JP and getNPQESStepperValue for NP.
  // For JP, verify (started/completed) returns null so the stepper does not move.
  const stageStepperValue = currentStage
    ? isLegalPerson
      ? getJPQESStepperValue({ currentStage, stageStatus })
      : getNPQESStepperValue({ currentStage, stageStatus })
    : null;

  const currentStep =
    (isLegalPerson || isExceptionalJP) &&
    currentStage === QES_CURRENT_STAGE.VERIFY
      ? 0 // Company verification is not completion of the signing process.
      : stageStepperValue !== null && stageStepperValue !== undefined
      ? stageStepperValue
      : currentSigningCase?.status === SIGNING_CASE_STATUS.PENDING_VERIFICATION
      ? 1 // Verification in progress
      : currentSigningCase?.status ===
        SIGNING_CASE_STATUS.AWAITING_SIGNER_SELECTION
      ? 2 // Awaiting signer selection
      : currentSigningCase?.status === SIGNING_CASE_STATUS.PROCESSING
      ? 2 // Signing in progress
      : currentSigningCase?.status === SIGNING_CASE_STATUS.RECEIVED
      ? 4 // Signing completed
      : currentSigningCase?.status === SIGNING_CASE_STATUS.FAILED
      ? 2 // Failed (show progress up to signing stage)
      : 0;

  // Download signed document

  const renderProgress = () => (
    <QESStepperScreen
      currentStep={currentStep}
      currentSigningCase={currentSigningCase}
      isCompletedScreen={isCompletedScreen}
      docsToDisplay={docsToDisplay}
      vollmachtSigningStatus={vollmachtSigningStatus}
      onCancelSigning={cancelSigning}
      onDownloadSigningDocument={downloadSignedDocument}
      halterFormValues={halterFormValues}
    />
  );
  const renderCompanyEntry = () => (
    <div className={classes.entryPanel}>
      <QESIdentitySetupForm
        isDetailDialog={isDetailDialog}
        personenform={
          isExceptionalJP ? formConstants.naturalPerson : personenform
        }
        firstName={firstName}
        lastName={lastName}
        companyName={companyName}
        email={email}
        emailError={emailError}
        hrab={isExceptionalJP ? "" : hrab}
        hrabError={isExceptionalJP ? "" : hrabError}
        validationMessages={validationMessages}
        loading={verifyingCompany || disabled}
        onEmailChange={(event) => {
          setEmail(event.target.value);
          setEmailError("");
        }}
        onHrabChange={(event) => setHrab(event.target.value)}
        onDownloadPrefilledDocuments={
          onDownloadPrefilledDocuments || downloadPrefilledDocuments
        }
        onStartSigningProcess={startCompanyVerification}
        disabled={disabled}
      />
    </div>
  );

  // Company Selection [can be different component]
  const renderCompanySelection = () => {
    const isVerifyingStage =
      currentStage === "verify" && stageStatus?.toLowerCase() !== "completed";

    return (
      <div>
        <div className={classes.sectionTitle}>
          Wählen Sie das Unternehmen aus
        </div>
        <div className={classes.companyGrid}>
          <MDCRadioButton
            name="verified-company"
            selected={selectedCompany?.id || ""}
            onChange={(event) => {
              if (!isVerifyingStage) {
                setSelectedCompany(
                  companyOption.id === event.target.value ? companyOption : null
                );
              }
            }}
            disabled={isVerifyingStage}
            options={[
              {
                value: companyOption.id,
                label: (
                  <span className={classes.companyOptionLabel}>
                    <span
                      className={classes.companyName}
                      title={companyOption.name}
                    >
                      {companyOption.name}
                    </span>
                    {companyOption.address && (
                      <span
                        className={classes.companyAddress}
                        title={companyOption.address}
                      >
                        {companyOption.address}
                      </span>
                    )}
                    {hrab && (
                      <div className={classes.hraHrb}>
                        <span>HRA/HRB: </span>
                        <span className={classes.hraHrbValue}>{hrab}</span>
                      </div>
                    )}
                  </span>
                ),
              },
            ]}
          />
        </div>
        {vollmachtSigningStatus?.errorMessage && (
          <ErrorText error={vollmachtSigningStatus.errorMessage} />
        )}
        {flowError && <div className={classes.error}>{flowError}</div>}
        <div className={classes.navigationActions}>
          <MDCButton
            variant="outlined"
            label="Zurück"
            onClick={() => setCompanyStep("entry")}
            disabled
          />

          <MDCButton
            variant="contained"
            label="Weiter"
            onClick={continueToSigners}
            disabled={disabled || !hasSignatories || isVerifyingStage}
          />
        </div>
      </div>
    );
  };

  const shouldShowProgress =
    currentSigningCase || showProgressScreen || currentStage === "sign";

  return (
    <div className={classes.container}>
      {shouldShowProgress ? (
        renderProgress()
      ) : isLegalPerson && !isExceptionalJP ? (
        companyStep === "entry" ? (
          renderCompanyEntry()
        ) : companyStep === "company-selection" ? (
          renderCompanySelection()
        ) : (
          <SignerSelectionScreen
            isDetailDialog={isDetailDialog}
            requestId={requestId}
            representatives={
              vollmachtSigningStatus?.providerDetails?.signatories || []
            }
            provider={provider}
            isPPOA={isPPOA}
            onGetPPOASigningStatus={onGetPPOASigningStatus}
            onComplete={() => setShowProgressScreen(true)}
            onBack={() => setCompanyStep("company-selection")}
            classes={classes}
            documentsAvailable={documentsAvailable}
            addressDocumentAvailable={addressDocumentAvailable}
            companyName={selectedCompany?.name || companyName}
            hrab={hrab}
            disabled={disabled}
          />
        )
      ) : (
        <div className={classes.entryPanel}>
          <QESIdentitySetupForm
            isDetailDialog={isDetailDialog}
            personenform={
              isExceptionalJP ? formConstants.naturalPerson : personenform
            }
            firstName={firstName}
            lastName={lastName}
            companyName={companyName}
            email={email}
            emailError={emailError}
            hrab=""
            hrabError=""
            validationMessages={validationMessages}
            loading={loading || disabled || isSubmittingNP}
            onEmailChange={(event) => {
              setEmail(event.target.value);
              setEmailError("");
            }}
            onHrabChange={() => {}}
            onDownloadPrefilledDocuments={downloadPrefilledDocuments}
            onStartSigningProcess={
              isExceptionalJP
                ? startCompanyVerification
                : startNaturalPersonSigning
            }
            disabled={disabled || isSubmittingNP}
          />
        </div>
      )}
    </div>
  );
};

// export default YouSignSigningFlow;
export default SigningStartScreen;
